import * as React from 'react';
import { ChangeEvent, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  EquipmentFileType,
  TEquipmentState,
} from '../../types/Equipment';
import { simpleDateFormat } from '../../Helpers';
import { fetchWithAuthentication } from '../../actions/BaseAction';
import { useEquipmentStyles } from './Equipment.styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from '../../store/Store';
import { TAuthState } from '../../types/Auth';
import { TDispatch } from '../../types/Thunk';
import { uploadEquipmentImage } from '../../actions/EquipmentActions';
import { useUser } from '../../hooks/useUser';
import EntityImageCapture from '../EntityImageCapture';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import Accordion from '@mui/material/Accordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import AccordionDetails from '@mui/material/AccordionDetails';
import ListItemIcon from '@mui/material/ListItemIcon';
import DescriptionIcon from '@mui/icons-material/Description';
import { getConclusionIcon } from '../../helpers/conclusionStatus';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

interface IEquipmentProps {
  equipmentState: TEquipmentState;
  expandedInstructions: boolean;
  expandedDocumentation: boolean;
  handleChange: (
    panel: string
  ) => ((event: ChangeEvent<{}>, expanded: boolean) => void) | undefined;
}

const Equipment: React.FC<IEquipmentProps> = ({
  equipmentState,
  expandedInstructions,
  expandedDocumentation,
  handleChange,
}): JSX.Element => {
  const [urlCache, setUrlCache] = useState<{ [key: string]: string }>({});
  const classes = useEquipmentStyles({});
  const { t } = useTranslation();
  const authState: TAuthState = useSelector<IAppState, TAuthState>(
    (state) => state.authState
  );
  const dispatch = useDispatch<TDispatch>();
  const { isImpersonated } = useUser();

  const handleFileClick = (filePath: string) => () => {
    if (urlCache[filePath]) {
      window.open(urlCache[filePath], '_blank');
      return;
    }

    fetchWithAuthentication(filePath)
      .then((blob) => {
        if (blob !== null) {
          const blobUrl = URL.createObjectURL(blob);
          setUrlCache((prevCache) => ({ ...prevCache, [filePath]: blobUrl }));
          window.open(blobUrl, '_blank');
        }
      })
      .catch((error) => {
        throw error;
      });
  };

  const active = equipmentState.activeEquipment;
  if (!active) return <></>;

  const instructionFiles = active.data.equipmentFiles?.filter(
    (f) => f.type === EquipmentFileType.INSTRUCTION
  );
  const documentFiles = active.data.equipmentFiles?.filter(
    (f) => f.type === EquipmentFileType.DOCUMENT
  );

  return (
    <>
      {active.data.name && <h2>{active.data.name}</h2>}
      {active.data.model && (
        <Typography variant="subtitle1" color="textSecondary">
          {active.data.model}
        </Typography>
      )}

      <EntityImageCapture
        imageUrl={active.data.image}
        canEdit={Boolean(authState.data?.permissions?.canEditEquipmentImages) && !isImpersonated()}
        placeholderIconClass='fas fa-wrench'
        widthOnDesktop='50%'
        onUpload={(dataUrl) => dispatch(uploadEquipmentImage(active.data.id, dataUrl))}
        altText={active.data.name}
      />

      {active.data.description && (
        <Typography
          style={{ marginTop: '15px' }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(active.data.description) }}
        />
      )}

      {active.ppe.length > 0 && (
        <div className={classes.protectiveMeasuresContainer}>
          {active.ppe.map((e) => (
            <ListItem key={e.label}>
              <ListItemAvatar>
                <Avatar className={classes.hazardIcon}>
                  <img src={e.image} alt={e.label} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={e.label} secondary={e.description} />
            </ListItem>
          ))}
        </div>
      )}

      {active.prohibitions.length > 0 && (
        <div className={classes.protectiveMeasuresContainer}>
          {active.prohibitions.map((e) => (
            <ListItem key={e.label}>
              <ListItemAvatar>
                <Avatar className={classes.hazardIcon}>
                  <img src={e.image} alt={e.label} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={e.label} secondary={e.description} />
            </ListItem>
          ))}
        </div>
      )}

      <div className={classes.serviceSection}>
        <Typography variant="subtitle2" className={classes.serviceSectionTitle}>
          <strong>{t('equipment.app.service')}</strong>
        </Typography>
        {active.latestService ? (
          <div className={classes.serviceRow}>
            {getConclusionIcon(active.latestService.statusValue)}
            <Typography style={{ marginLeft: '8px' }}>
              {active.latestService.status}
              {'    '}
              {active.latestService.date} {active.latestService.user}
            </Typography>
          </div>
        ) : null}
        {active.nextServiceDate ? (
          <Typography color="textSecondary">
            {t('equipment.app.next_service')}: {active.nextServiceDate}
          </Typography>
        ) : null}
        {!active.latestService && !active.nextServiceDate && (
          <Typography color="textSecondary">
            {t('equipment.app.no_service')}
          </Typography>
        )}
      </div>

      {(active.data.instructions || (instructionFiles && instructionFiles.length > 0)) && (
        <Accordion
          expanded={expandedInstructions}
          onChange={handleChange('expandedEqInstructions')}
          key={'expandedEqInstructions'}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="expandedEqInstructions-content"
            id="expandedEqInstructions-header"
          >
            <Typography className={classes.heading}>
              {t('equipment.app.instructions')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            {active.data.instructions && (
              <Typography
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(active.data.instructions),
                }}
              />
            )}
            {instructionFiles &&
              instructionFiles.map((file) => (
                <ListItem
                  key={file.id}
                  role={undefined}
                  dense
                  button
                  onClick={handleFileClick(file.media)}
                >
                  <ListItemIcon>
                    <DescriptionIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.title}
                    secondary={file.notes ? file.notes : null}
                  />
                </ListItem>
              ))}
          </AccordionDetails>
        </Accordion>
      )}

      {documentFiles && documentFiles.length > 0 && (
        <Accordion
          expanded={expandedDocumentation}
          onChange={handleChange('expandedEqDocumentation')}
          key={'expandedEqDocumentation'}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="expandedEqDocumentation-content"
            id="expandedEqDocumentation-header"
          >
            <Typography className={classes.heading}>
              {t('equipment.app.documentation')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            {documentFiles.map((file) => (
              <ListItem
                key={file.id}
                role={undefined}
                dense
                button
                onClick={handleFileClick(file.media)}
              >
                <ListItemIcon>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.title}
                  secondary={file.notes ? file.notes : null}
                />
              </ListItem>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      <Table className={classes.detailTable}>
        <TableBody>
          {active.data.serialVin && (
            <TableRow>
              <TableCell>{t('equipment.app.serial_vin')}</TableCell>
              <TableCell align="right">{active.data.serialVin}</TableCell>
            </TableRow>
          )}
          {active.data.licenseNumber && (
            <TableRow>
              <TableCell>{t('equipment.app.license_number')}</TableCell>
              <TableCell align="right">{active.data.licenseNumber}</TableCell>
            </TableRow>
          )}
          {active.data.internalId && (
            <TableRow>
              <TableCell>{t('equipment.app.internal_id')}</TableCell>
              <TableCell align="right">{active.data.internalId}</TableCell>
            </TableRow>
          )}
          {active.data.manufacturer && (
            <TableRow>
              <TableCell>{t('equipment.app.manufacturer')}</TableCell>
              <TableCell align="right">{active.data.manufacturer.name}</TableCell>
            </TableRow>
          )}
          {active.data.supplier && (
            <TableRow>
              <TableCell>{t('equipment.app.supplier')}</TableCell>
              <TableCell align="right">{active.data.supplier.name}</TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>{t('equipment.app.ce_marking')}</TableCell>
            <TableCell align="right">
              {active.data.ceMarking
                ? t('equipment.app.yes')
                : t('equipment.app.no')}
            </TableCell>
          </TableRow>
          {active.data.purchaseDate && (
            <TableRow>
              <TableCell>{t('equipment.app.purchase_date')}</TableCell>
              <TableCell align="right">
                {simpleDateFormat(active.data.purchaseDate)}
              </TableCell>
            </TableRow>
          )}
          {active.data.warrantyExpires && (
            <TableRow>
              <TableCell>{t('equipment.app.warranty_expires')}</TableCell>
              <TableCell align="right">
                {simpleDateFormat(active.data.warrantyExpires)}
              </TableCell>
            </TableRow>
          )}
          {active.data.lastRevised && (
            <TableRow>
              <TableCell>{t('equipment.app.last_revised')}</TableCell>
              <TableCell align="right">
                {simpleDateFormat(active.data.lastRevised)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

export default Equipment;
